import "./tutorial.scss";

import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { Component } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import FormButton from "../../components/form/FormButton";
import Icon from "../../components/interface/Icon";
import PubSubTopics from "../../constants/pubSubTopics";
import Dimensions from "../../constants/styles/dimensions";
import { appStateOperations, appStateSelectors } from "../../modules/appState";
import { batchActions } from "../../modules/chainReducer";
import { definitionSelectors } from "../../modules/definition";
import { narrativeSettingsSelectors } from "../../modules/definition/narrativeSettings";
import { typeSelectors } from "../../modules/definition/types";
import { syncSettingsSelectors } from "../../modules/sync/settings";
import { tutorialOperations, tutorialSelectors } from "../../modules/tutorial";
import { uiStateSelectors } from "../../modules/uiState";
import { nativeCommsBroadcastMessageTypes } from "../../nativeComms";
import { tutorialDisplaySelectors } from "../../tutorial/selectors";
import { replaceTutorialPlaceholders } from "../../tutorial/utils";
import { PropTypeShapes } from "../../types";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import hasAncestorOfClass, { hasAncestorWithId } from "../../utils/hasAncestorOfClass";
import openUrl from "../../utils/openUrl";
import preprocessorQuery from "../../utils/preprocessorQuery";
import uniqueId from "../../utils/uniqueId";
import sidebarSelectors from "../sidebar/selectors";
import TutorialTipView from "./TutorialTipView";

const isDesktop = preprocessorQuery.isDesktop();
const isTablet = preprocessorQuery.isTablet();

let tutorialTimer;

const SEE_MORE_TUTORIALS_ID = "seeMoreTutorials";

const clickWasButtonOrLink = e =>
  (hasAncestorOfClass(e.target, "formBtn") ||
    hasAncestorOfClass(e.target, "tipEmbeddedFlashLink") ||
    hasAncestorOfClass(e.target, "tipEmbeddedExternalLink")) &&
  !hasAncestorWithId(e.target, SEE_MORE_TUTORIALS_ID);

class TutorialNotification extends Component {
  constructor(props) {
    super(props);

    this.state = {
      activeTip: props.activeTip,
      tipCompleted: false,
      tipIsDismissable: false,
      completedGroupId: null
    };

    this._lastClickWasNext = false;

    this.showActiveTip = this.showActiveTip.bind(this);
    this.showActiveTipWithDelay = this.showActiveTipWithDelay.bind(this);
    this.completeTutorial = this.completeTutorial.bind(this);
    this.onNotificationClick = this.onNotificationClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onCompletedTutorialClick = this.onCompletedTutorialClick.bind(this);
    this.onDismissCompletedClick = this.onDismissCompletedClick.bind(this);
    this.onShowNextTutorialClick = this.onShowNextTutorialClick.bind(this);
    this.getTextWithReplacements = this.getTextWithReplacements.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.activeTip && nextProps.activeTip && this.props.activeTip.id !== nextProps.activeTip.id) {
      // If we were already showing a tip and we're moving to a different tip, delay the transition
      // unless the user clicked "next" inside this component
      if (this._lastClickWasNext) {
        this.showActiveTip(nextProps);
      } else {
        this.showActiveTipWithDelay(nextProps);
      }
    } else if (nextProps.activeTip) {
      // If there was no previous tip, just show the new tip
      this.showActiveTip(nextProps);
    } else if (this.props.activeTip) {
      // If there was a previous tip bu there's no new tip, check if we should show the completed message

      const groupIsComplete = nextProps.groups.byId.get(this.props.activeTutorial).isComplete;
      if (groupIsComplete) {
        this.completeTutorial(this.props.activeTutorial);
      }
    }
    this._lastClickWasNext = false;
  }

  componentWillUnmount() {
    clearTimeout(tutorialTimer);
  }

  getTextWithReplacements(text) {
    return replaceTutorialPlaceholders(
      text,
      this.props.sidebarTypes,
      this.props.orderedNarrativeTypes,
      this.props.typesById,
      this.props.defaultSequenceType,
      this.props.narrativeSectionTypes,
      this.props.syncAppName
    );
  }

  showActiveTip(props) {
    clearTimeout(tutorialTimer);
    this.setState({
      activeTip: props.activeTip,
      tipIsDismissable: props.activeTipIsDismissable,
      completedGroupId: null,
      tipCompleted: false
    });
  }

  showActiveTipWithDelay(props) {
    clearTimeout(tutorialTimer);
    this.setState({ tipCompleted: true }, () => {
      tutorialTimer = setTimeout(() => {
        this.setState({
          activeTip: props.activeTip,
          tipIsDismissable: props.activeTipIsDismissable,
          completedGroupId: null,
          tipCompleted: false
        });
      }, 3000);
    });
  }

  completeTutorial(groupId) {
    clearTimeout(tutorialTimer);
    this.setState({ completedGroupId: groupId });
    // this.setState({ completedGroupId: groupId }, () => {
    //   tutorialTimer = setTimeout(() => {
    //     this.setState({ activeTip: null, completedGroupId: null, tipCompleted: false });
    //   }, 10000);
    // });
  }

  onNotificationClick(e) {
    if (!clickWasButtonOrLink(e)) {
      this.props.showTutorial();
    }
  }

  onNextClick() {
    this._lastClickWasNext = true;
  }

  onCompletedTutorialClick(e) {
    if (!clickWasButtonOrLink(e)) {
      clearTimeout(tutorialTimer);
      this.setState({ activeTip: null, completedGroupId: null, tipCompleted: false });
      this.props.showTutorial();
    }
  }

  onShowNextTutorialClick(tutorialId) {
    clearTimeout(tutorialTimer);
    this.setState({ activeTip: null, completedGroupId: null, tipCompleted: false });
    this.props.showNextTutorial(tutorialId);
  }

  onDismissCompletedClick() {
    clearTimeout(tutorialTimer);
    this.setState({ activeTip: null, completedGroupId: null, tipCompleted: false });
  }

  renderCompletedTutorial() {
    const { groups, notification } = this.props;
    const { completedGroupId } = this.state;

    const completedGroup = groups.byId.get(completedGroupId);
    const showCompact = false;
    const activeIndex = groups.allIds.indexOf(completedGroupId);
    const nextTutorialId = groups.allIds.get(activeIndex + 1);
    let nextTutorialLabel = null;
    if (nextTutorialId) {
      nextTutorialLabel = groups.byId.get(nextTutorialId).label;
    }

    let link = null;
    const tutorialLinks = tutorialDisplaySelectors.getGroupLinks(completedGroupId);
    if (tutorialLinks && tutorialLinks.length) {
      link = tutorialLinks[0];
    }

    return (
      <div
        className={`tutorialNotification ${showCompact ? "compact" : ""}`}
        key={uniqueId()}
        onClick={this.onCompletedTutorialClick}
      >
        <div className="tutorialIconBlock">
          <Icon icon="tutorialIconBlock" color="iconMono" />
          <div>
            <div className="tipTitle">
              {/* {tickIcon} */}
              <span className="notificationMessage">{completedGroup.label} tutorial complete</span>
            </div>
            <div className="tipDescription">{this.getTextWithReplacements(completedGroup.completedMessage)}</div>

            {link ? (
              <div className="tipLink">
                <FormButton
                  label={link.label}
                  linkStyle={true}
                  allowTextWrap={true}
                  onClick={() => {
                    openUrl(link.path);
                  }}
                />
              </div>
            ) : null}
            <div className="tipNext">
              {nextTutorialId ? (
                <FormButton
                  label={`Up Next: ${nextTutorialLabel}`}
                  primary={true}
                  onClick={() => this.onShowNextTutorialClick(nextTutorialId)}
                />
              ) : (
                <FormButton
                  id={SEE_MORE_TUTORIALS_ID}
                  label="See more tutorials"
                  linkStyle={true}
                  onClick={this.onCompletedTutorialClick}
                />
              )}
            </div>
          </div>
        </div>
        {!notification ? (
          <div className="notificationButtonsContainer">
            <div className="notificationCloseButtonContainer">
              <FormButton onClick={this.onDismissCompletedClick} label="Close" linkStyle={true} />
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  renderTutorialTip() {
    const { onCloseClick, notification } = this.props;
    const { activeTip, tipCompleted } = this.state;

    // Move the notification left if the inspector is open and an item is selected
    // Show a compact notification if it's being shown above a NotificationView
    const moveToAvoidSidebar =
      (isDesktop && this.props.isItemInspectorOpen && this.props.selectedItemIds.size && activeTip.requiresInspector) ||
      (isTablet && (this.props.rightPanelMode || this.props.leftPanelMode));
    const showCompact = notification && !moveToAvoidSidebar;
    const style = moveToAvoidSidebar
      ? { marginRight: this.props.rightPanelWidth + (isTablet ? this.props.sidebarWidth : 0) + "px" }
      : null;

    return (
      <div
        className={`tutorialNotification ${tipCompleted ? "completed" : ""} ${showCompact ? "compact" : ""}`}
        style={style}
        key={uniqueId()}
        onClick={this.onNotificationClick}
      >
        <TutorialTipView
          tip={activeTip}
          className={"notificationMessage"}
          tipCompleted={tipCompleted}
          isNotification={true}
          isActive={true}
          activeTutorial={this.props.activeTutorial}
          tipIsDismissable={this.state.tipIsDismissable}
          showExpandIcon={false}
          showDetail={!notification}
          onExpandClick={null}
          sidebarTypes={this.props.sidebarTypes}
          orderedNarrativeTypes={this.props.orderedNarrativeTypes}
          typesById={this.props.typesById}
          defaultSequenceType={this.props.defaultSequenceType}
          narrativeSectionTypes={this.props.narrativeSectionTypes}
          syncAppName={this.props.syncAppName}
          isEditMode={this.props.isEditMode}
          blockedTips={this.props.blockedTips}
          completedTipSet={this.props.completedTipSet}
          dispatchAction={this.props.dispatchAction}
          onNextClick={this.onNextClick}
        />
        {!notification ? (
          <div className="notificationButtonsContainer">
            <div className="notificationCloseButtonContainer">
              <FormButton onClick={onCloseClick} label="Mute" linkStyle={true} />
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  render() {
    const { showTutorialNotification } = this.props;
    const { completedGroupId } = this.state;

    if (!showTutorialNotification && !completedGroupId) {
      return null;
    }

    if (completedGroupId) {
      return this.renderCompletedTutorial();
    }

    return this.renderTutorialTip();
  }
}

TutorialNotification.propTypes = {
  groups: PropTypes.object,
  activeTutorial: PropTypes.string,
  nextTutorial: PropTypes.string,
  activeTip: PropTypes.object,
  activeTipIsDismissable: PropTypes.bool,
  onCloseClick: PropTypes.func.isRequired,
  dispatchAction: PropTypes.func.isRequired,
  blockedTips: PropTypes.object,
  completedTipSet: ImmutablePropTypes.set.isRequired,
  defaultSequenceType: PropTypes.string.isRequired,
  typesById: ImmutablePropTypes.map.isRequired,
  orderedPersonTypes: ImmutablePropTypes.list,
  orderedNarrativeTypes: ImmutablePropTypes.list,
  narrativeSectionTypes: ImmutablePropTypes.list,
  sidebarTypes: ImmutablePropTypes.list,
  showTutorialNotification: PropTypes.bool,
  notification: PropTypeShapes.uiNotification,
  leftPanelMode: PropTypes.string,
  rightPanelMode: PropTypes.string,
  rightPanelWidth: PropTypes.number,
  isItemInspectorOpen: PropTypes.bool,
  sidebarWidth: PropTypes.number,
  selectedItemIds: ImmutablePropTypes.list.isRequired,
  showTutorial: PropTypes.func.isRequired,
  showNextTutorial: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,
  syncAppName: PropTypes.string
};

const mapStateToProps = state => {
  const data = tutorialDisplaySelectors.getTutorialDisplayStatus(state.present);
  return {
    groups: data.groups,
    activeTutorial: data.activeTutorial,
    activeTip: tutorialDisplaySelectors.getTutorialDisplayStatusActiveTip(state.present),
    activeTipIsDismissable: tutorialDisplaySelectors.getTutorialActiveTipIsDismissable(state.present),
    blockedTips: tutorialDisplaySelectors.getAccessRestrictedTipsInActiveGroup(state.present),
    completedTipSet: tutorialSelectors.getCompletedTips(state.present),
    defaultSequenceType: definitionSelectors.getDefaultSequenceType(state.present),
    typesById: typeSelectors.getTypesById(state.present),
    orderedPersonTypes: typeSelectors.getOrderedPersonTypes(state.present),
    orderedNarrativeTypes: typeSelectors.getOrderedNarrativeTypes(state.present),
    narrativeSectionTypes: narrativeSettingsSelectors.getNarrativeLabels(state.present),
    sidebarTypes: typeSelectors.getSidebarTypes(state.present),
    showTutorialNotification: tutorialDisplaySelectors.getShowTutorialNotification(state.present),
    notification: uiStateSelectors.getUiStateNotification(state.present),
    leftPanelMode: appStateSelectors.getLeftPanelMode(state.present),
    rightPanelMode: appStateSelectors.getRightPanelMode(state.present),
    //#!if desktop === true
    rightPanelWidth: appStateSelectors.getRightPanelWidth(state.present),
    //#!else
    // eslint-disable-next-line no-dupe-keys
// rightPanelWidth: Dimensions.DEFAULT_RIGHT_PANEL_WIDTH,
    //#!endif
    isItemInspectorOpen: appStateSelectors.isItemInspectorOpen(state.present),
    sidebarWidth: sidebarSelectors.getSidebarWidth(0),
    selectedItemIds: uiStateSelectors.getSelectedItems(state.present),
    isEditMode: uiStateSelectors.getIsEditMode(state.present),
    syncAppName: syncSettingsSelectors.getSyncApplicationName(state.present)
  };
};

const readonlyActions = {};

const editingActions = {
  onCloseClick: () => {
    PubSub.publish(PubSubTopics.sendNativeMessage, {
      msg: nativeCommsBroadcastMessageTypes.SILENCE_TUTORIAL,
      silenced: true
    });
    return tutorialOperations.setTutorialSilenced(true);
  },
  dispatchAction: action => action,
  showTutorial: appStateOperations.showTutorial,
  showNextTutorial: tutorialId => {
    let actions = [appStateOperations.showTutorial()];
    if (tutorialId) {
      actions.push(tutorialOperations.setVisibleTutorial(tutorialId));
    }
    return batchActions(actions);
  }
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(TutorialNotification);
